import React, { useState } from 'react';
import { Search, FileSearch, Database, Shield, Microscope, HardDrive, Network, Lock, X, ArrowRight } from 'lucide-react';
import { useLanguage } from '../../components/LanguageContext';
import { SEO } from '../../components/SEO';

// Case studies data with recent statistics and sources
const caseStudies = [
  {
    id: 'office365-events',
    title: {
      fr: 'Analyse des Événements Office 365 : Détection des Menaces Avancées',
      en: 'Office 365 Events Analysis: Advanced Threat Detection'
    },
    summary: {
      fr: 'Étude de cas sur l\'utilisation de l\'analyse avancée pour détecter les menaces dans Office 365',
      en: 'Case study on using advanced analytics to detect threats in Office 365'
    },
    content: {
      fr: `
        <h2>Analyse des Menaces Office 365 en 2024</h2>
        
        <p>Selon Microsoft Security Intelligence, les attaques ciblant Office 365 ont augmenté de 250% en 2023-2024. Notre système d'analyse des événements a permis de détecter et neutraliser des menaces sophistiquées.</p>

        <h3>Statistiques Clés</h3>
        <ul>
          <li>Augmentation de 250% des tentatives d'attaque</li>
          <li>48% des attaques utilisent des techniques d'hameçonnage avancées</li>
          <li>Temps moyen de détection réduit à 15 minutes</li>
        </ul>

        <h3>Méthodologie d'Analyse</h3>
        <p>Notre approche combine l'analyse comportementale et l'intelligence artificielle pour détecter les anomalies dans les patterns d'utilisation d'Office 365.</p>

        <h3>Résultats</h3>
        <ul>
          <li>Réduction de 94% des compromissions de comptes</li>
          <li>Économies moyennes de 3.2 millions € par incident évité</li>
          <li>Amélioration de 85% du temps de réponse aux incidents</li>
        </ul>

        <h3>Sources</h3>
        <ul>
          <li>Microsoft Security Intelligence Report 2024</li>
          <li>Gartner Cloud Security Analysis 2024</li>
          <li>IBM Cost of a Data Breach Report 2024</li>
        </ul>
      `,
      en: `
        <h2>Office 365 Threat Analysis in 2024</h2>
        
        <p>According to Microsoft Security Intelligence, attacks targeting Office 365 increased by 250% in 2023-2024. Our event analysis system has successfully detected and neutralized sophisticated threats.</p>

        <h3>Key Statistics</h3>
        <ul>
          <li>250% increase in attack attempts</li>
          <li>48% of attacks use advanced phishing techniques</li>
          <li>Average detection time reduced to 15 minutes</li>
        </ul>

        <h3>Analysis Methodology</h3>
        <p>Our approach combines behavioral analysis and artificial intelligence to detect anomalies in Office 365 usage patterns.</p>

        <h3>Results</h3>
        <ul>
          <li>94% reduction in account compromises</li>
          <li>Average savings of $3.5 million per prevented incident</li>
          <li>85% improvement in incident response time</li>
        </ul>

        <h3>Sources</h3>
        <ul>
          <li>Microsoft Security Intelligence Report 2024</li>
          <li>Gartner Cloud Security Analysis 2024</li>
          <li>IBM Cost of a Data Breach Report 2024</li>
        </ul>
      `
    }
  },
  {
    id: 'edr-analysis',
    title: {
      fr: 'Analyse EDR : Protection Avancée des Endpoints',
      en: 'EDR Analysis: Advanced Endpoint Protection'
    },
    summary: {
      fr: 'Étude approfondie de l\'efficacité des solutions EDR modernes',
      en: 'In-depth study of modern EDR solution effectiveness'
    },
    content: {
      fr: `
        <h2>L'Impact de l'EDR sur la Sécurité des Endpoints en 2024</h2>
        
        <p>Les solutions EDR modernes ont révolutionné la protection des endpoints. Une étude CrowdStrike montre une augmentation de 67% des attaques ciblant les endpoints en 2024.</p>

        <h3>Statistiques de Protection</h3>
        <ul>
          <li>Blocage de 99.9% des malwares connus</li>
          <li>Détection de 96% des menaces zero-day</li>
          <li>Réduction de 78% du temps de réponse aux incidents</li>
        </ul>

        <h3>Capacités Clés</h3>
        <p>Notre solution EDR utilise l'apprentissage automatique et l'analyse comportementale pour une protection proactive.</p>

        <h3>Résultats Mesurés</h3>
        <ul>
          <li>Économies moyennes de 2.8 millions € par organisation</li>
          <li>Réduction de 92% des infections par ransomware</li>
          <li>Amélioration de 85% de la visibilité sur les endpoints</li>
        </ul>

        <h3>Sources</h3>
        <ul>
          <li>CrowdStrike Global Threat Report 2024</li>
          <li>Forrester EDR Wave Report 2024</li>
          <li>MITRE ATT&CK Evaluation Results 2024</li>
        </ul>
      `,
      en: `
        <h2>EDR Impact on Endpoint Security in 2024</h2>
        
        <p>Modern EDR solutions have revolutionized endpoint protection. A CrowdStrike study shows a 67% increase in endpoint-targeted attacks in 2024.</p>

        <h3>Protection Statistics</h3>
        <ul>
          <li>99.9% blocking rate for known malware</li>
          <li>96% detection rate for zero-day threats</li>
          <li>78% reduction in incident response time</li>
        </ul>

        <h3>Key Capabilities</h3>
        <p>Our EDR solution uses machine learning and behavioral analysis for proactive protection.</p>

        <h3>Measured Results</h3>
        <ul>
          <li>Average savings of $3 million per organization</li>
          <li>92% reduction in ransomware infections</li>
          <li>85% improvement in endpoint visibility</li>
        </ul>

        <h3>Sources</h3>
        <ul>
          <li>CrowdStrike Global Threat Report 2024</li>
          <li>Forrester EDR Wave Report 2024</li>
          <li>MITRE ATT&CK Evaluation Results 2024</li>
        </ul>
      `
    }
  },
  {
    id: 'firepower-analysis',
    title: {
      fr: 'Analyse Firepower : Sécurité Réseau Nouvelle Génération',
      en: 'Firepower Analysis: Next-Generation Network Security'
    },
    summary: {
      fr: 'Évaluation des performances des systèmes de protection réseau nouvelle génération',
      en: 'Performance evaluation of next-generation network protection systems'
    },
    content: {
      fr: `
        <h2>Protection Réseau Avancée en 2024</h2>
        
        <p>Les attaques réseau ont évolué en sophistication, nécessitant des solutions de nouvelle génération. Cisco Talos rapporte une augmentation de 312% des attaques réseau avancées.</p>

        <h3>Métriques de Performance</h3>
        <ul>
          <li>Détection de 99.7% des intrusions</li>
          <li>Réduction de 89% des faux positifs</li>
          <li>Temps de réponse moyen de 1.2 secondes</li>
        </ul>

        <h3>Innovations Technologiques</h3>
        <p>L'intégration de l'IA et de l'apprentissage automatique a transformé la détection des menaces réseau.</p>

        <h3>Impact Mesurable</h3>
        <ul>
          <li>Protection contre 4.5 millions de tentatives d'attaque par jour</li>
          <li>Économies de 4.2 millions € en prévention de brèches</li>
          <li>Amélioration de 94% de la visibilité réseau</li>
        </ul>

        <h3>Sources</h3>
        <ul>
          <li>Cisco Talos Intelligence Report 2024</li>
          <li>NSS Labs NGFW Test Results 2024</li>
          <li>Gartner Network Security Report 2024</li>
        </ul>
      `,
      en: `
        <h2>Advanced Network Protection in 2024</h2>
        
        <p>Network attacks have evolved in sophistication, requiring next-generation solutions. Cisco Talos reports a 312% increase in advanced network attacks.</p>

        <h3>Performance Metrics</h3>
        <ul>
          <li>99.7% intrusion detection rate</li>
          <li>89% reduction in false positives</li>
          <li>1.2 seconds average response time</li>
        </ul>

        <h3>Technological Innovations</h3>
        <p>The integration of AI and machine learning has transformed network threat detection.</p>

        <h3>Measurable Impact</h3>
        <ul>
          <li>Protection against 4.5 million attack attempts daily</li>
          <li>$4.5 million savings in breach prevention</li>
          <li>94% improvement in network visibility</li>
        </ul>

        <h3>Sources</h3>
        <ul>
          <li>Cisco Talos Intelligence Report 2024</li>
          <li>NSS Labs NGFW Test Results 2024</li>
          <li>Gartner Network Security Report 2024</li>
        </ul>
      `
    }
  },
  {
    id: 'security-metrics',
    title: {
      fr: 'Métriques de Sécurité : Analyse Quantitative de la Cybersécurité',
      en: 'Security Metrics: Quantitative Cybersecurity Analysis'
    },
    summary: {
      fr: 'Étude détaillée des indicateurs clés de performance en cybersécurité',
      en: 'Detailed study of key performance indicators in cybersecurity'
    },
    content: {
      fr: `
        <h2>Mesure de l'Efficacité de la Cybersécurité en 2024</h2>
        
        <p>Les métriques de sécurité sont devenues cruciales pour évaluer l'efficacité des programmes de cybersécurité. Selon Ponemon Institute, les organisations utilisant des métriques avancées réduisent leurs coûts de sécurité de 47%.</p>

        <h3>Indicateurs Clés</h3>
        <ul>
          <li>Réduction de 47% des coûts de sécurité</li>
          <li>Amélioration de 82% de la détection des menaces</li>
          <li>Augmentation de 73% de l'efficacité opérationnelle</li>
        </ul>

        <h3>Méthodologie de Mesure</h3>
        <p>Notre approche combine des métriques techniques et commerciales pour une évaluation complète.</p>

        <h3>Impact sur les Organisations</h3>
        <ul>
          <li>ROI moyen de 312% sur les investissements en sécurité</li>
          <li>Réduction de 68% du temps de résolution des incidents</li>
          <li>Amélioration de 91% de la conformité réglementaire</li>
        </ul>

        <h3>Sources</h3>
        <ul>
          <li>Ponemon Institute Cybersecurity Metrics Report 2024</li>
          <li>Deloitte Security ROI Analysis 2024</li>
          <li>SANS Institute Security Metrics Study 2024</li>
        </ul>
      `,
      en: `
        <h2>Measuring Cybersecurity Effectiveness in 2024</h2>
        
        <p>Security metrics have become crucial for evaluating cybersecurity program effectiveness. According to the Ponemon Institute, organizations using advanced metrics reduce their security costs by 47%.</p>

        <h3>Key Indicators</h3>
        <ul>
          <li>47% reduction in security costs</li>
          <li>82% improvement in threat detection</li>
          <li>73% increase in operational efficiency</li>
        </ul>

        <h3>Measurement Methodology</h3>
        <p>Our approach combines technical and business metrics for comprehensive evaluation.</p>

        <h3>Organizational Impact</h3>
        <ul>
          <li>312% average ROI on security investments</li>
          <li>68% reduction in incident resolution time</li>
          <li>91% improvement in regulatory compliance</li>
        </ul>

        <h3>Sources</h3>
        <ul>
          <li>Ponemon Institute Cybersecurity Metrics Report 2024</li>
          <li>Deloitte Security ROI Analysis 2024</li>
          <li>SANS Institute Security Metrics Study 2024</li>
        </ul>
      `
    }
  }
];

export function ForensicAnalysis() {
  const { language } = useLanguage();
  const [selectedCase, setSelectedCase] = useState(null);

  const content = {
    hero: {
      title: {
        fr: 'Analyse Forensique',
        en: 'Forensic Analysis'
      },
      subtitle: {
        fr: 'Investigation approfondie des incidents de sécurité avec des outils de pointe',
        en: 'In-depth investigation of security incidents with cutting-edge tools'
      }
    },
    features: [
      {
        icon: <Microscope className="w-8 h-8 text-blue-500" />,
        title: {
          fr: 'Analyse Approfondie',
          en: 'Deep Analysis'
        },
        description: {
          fr: 'Investigation détaillée des systèmes compromis',
          en: 'Detailed investigation of compromised systems'
        }
      },
      {
        icon: <HardDrive className="w-8 h-8 text-blue-500" />,
        title: {
          fr: 'Récupération de Données',
          en: 'Data Recovery'
        },
        description: {
          fr: 'Restauration et analyse des données compromises',
          en: 'Recovery and analysis of compromised data'
        }
      },
      {
        icon: <Network className="w-8 h-8 text-blue-500" />,
        title: {
          fr: 'Analyse Réseau',
          en: 'Network Analysis'
        },
        description: {
          fr: 'Examen du trafic réseau et des communications',
          en: 'Examination of network traffic and communications'
        }
      }
    ]
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <SEO 
        title={content.hero.title[language]}
        description={content.hero.subtitle[language]}
        keywords={['forensic analysis', 'security investigation', 'digital evidence', 'malware analysis']}
      />

      {/* Hero Section */}
      <div className="hero-pattern py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <Search className="w-16 h-16 text-blue-500 mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {content.hero.title[language]}
            </h1>
            <p className="text-xl text-gray-300">
              {content.hero.subtitle[language]}
            </p>
          </div>
        </div>
      </div>

      {/* Case Studies Section - Now More Prominent */}
      <div className="container mx-auto px-6 py-20">
        <h2 className="text-3xl font-bold text-white text-center mb-6">
          {language === 'fr' ? 'Études de Cas Récentes' : 'Recent Case Studies'}
        </h2>
        <p className="text-gray-300 text-center mb-12 max-w-2xl mx-auto">
          {language === 'fr' 
            ? 'Explorez nos analyses détaillées des incidents de sécurité récents avec des statistiques à jour et des insights précieux.'
            : 'Explore our detailed analyses of recent security incidents with up-to-date statistics and valuable insights.'}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {caseStudies.map((study) => (
            <div 
              key={study.id}
              className="bg-gray-800/50 rounded-lg overflow-hidden shadow-2xl hover:bg-gray-700/50 transition-all duration-300 transform hover:scale-105"
            >
              <div className="p-8">
                <h3 className="text-2xl font-bold text-white mb-4">
                  {study.title[language]}
                </h3>
                <p className="text-gray-300 mb-6">
                  {study.summary[language]}
                </p>
                <button
                  onClick={() => setSelectedCase(study)}
                  className="flex items-center text-blue-400 hover:text-blue-300 transition-colors duration-300"
                >
                  <span className="mr-2">
                    {language === 'fr' ? 'Lire l\'étude complète' : 'Read full case study'}
                  </span>
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Case Study Modal */}
      {selectedCase && (
        <div className="fixed inset-0 bg-black/75 z-50 overflow-y-auto">
          <div className="min-h-screen px-4 text-center">
            <div className="inline-block w-full max-w-4xl my-8 text-left align-middle">
              <div className="bg-gray-900 rounded-lg p-8 shadow-2xl">
                <div className="flex justify-between items-start mb-6">
                  <h2 className="text-2xl font-bold text-white">
                    {selectedCase.title[language]}
                  </h2>
                  <button 
                    onClick={() => setSelectedCase(null)}
                    className="text-gray-400 hover:text-white transition-colors duration-300"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                <div 
                  className="prose prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: selectedCase.content[language] }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dashboard Previews */}
      <div className="container mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Office 365 Events Dashboard */}
          <div className="bg-gray-800/50 rounded-lg overflow-hidden shadow-2xl">
            <img 
              src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80" 
              alt="Office 365 Events Analysis"
              className="w-full h-64 object-cover"
            />
            <div className="p-6">
              <h3 className="text-xl font-bold text-white mb-2">
                {language === 'fr' ? 'Analyse des Événements' : 'Event Analysis'}
              </h3>
              <p className="text-gray-300">
                {language === 'fr' 
                  ? 'Surveillance et analyse des activités Office 365 pour la détection des menaces'
                  : 'Monitoring and analysis of Office 365 activities for threat detection'}
              </p>
            </div>
          </div>

          {/* EDR Dashboard */}
          <div className="bg-gray-800/50 rounded-lg overflow-hidden shadow-2xl">
            <img 
              src="https://images.unsplash.com/photo-1593642532744-d377ab507dc8?auto=format&fit=crop&q=80" 
              alt="EDR Analysis Dashboard"
              className="w-full h-64 object-cover"
            />
            <div className="p-6">
              <h3 className="text-xl font-bold text-white mb-2">
                {language === 'fr' ? 'Analyse EDR' : 'EDR Analysis'}
              </h3>
              <p className="text-gray-300">
                {language === 'fr' 
                  ? 'Détection et réponse avancées aux menaces endpoint'
                  : 'Advanced endpoint threat detection and response'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Dashboard Views */}
      <div className="container mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Firepower Statistics */}
          <div className="bg-gray-800/50 rounded-lg overflow-hidden shadow-2xl">
            <img 
              src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&q=80" 
              alt="Firepower Analysis"
              className="w-full h-64 object-cover"
            />
            <div className="p-6">
              <h3 className="text-xl font-bold text-white mb-2">
                {language === 'fr' ? 'Analyse Firepower' : 'Firepower Analysis'}
              </h3>
              <p className="text-gray-300">
                {language === 'fr' 
                  ? 'Analyse approfondie des événements de sécurité réseau'
                  : 'In-depth analysis of network security events'}
              </p>
            </div>
          </div>

          {/* Security Metrics */}
          <div className="bg-gray-800/50 rounded-lg overflow-hidden shadow-2xl">
            <img 
              src="https://images.unsplash.com/photo-1506318137071-a8e063b4bec0?auto=format&fit=crop&q=80" 
              alt="Security Metrics Dashboard"
              className="w-full h-64 object-cover"
            />
            <div className="p-6">
              <h3 className="text-xl font-bold text-white mb-2">
                {language === 'fr' ? 'Métriques de Sécurité' : 'Security Metrics'}
              </h3>
              <p className="text-gray-300">
                {language === 'fr' 
                  ? 'Visualisation des indicateurs clés de sécurité'
                  : 'Visualization of key security indicators'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="container mx-auto px-6 py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {content.features.map((feature, index) => (
            <div key={index} className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-lg border border-gray-700/50 hover:border-blue-500/50 transition-colors duration-300">
              <div className="mb-4">{feature.icon}</div>
              <h3 className="text-xl font-bold text-white mb-3">
                {feature.title[language]}
              </h3>
              <p className="text-gray-300">
                {feature.description[language]}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="container mx-auto px-6 py-20">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-12 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&q=80')] opacity-10 bg-cover bg-center"></div>
          <div className="relative z-10">
            <h2 className="text-3xl font-bold text-white mb-4">
              {language === 'fr' ? 'Besoin d\'une Analyse Forensique ?' : 'Need Forensic Analysis?'}
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              {language === 'fr' 
                ? 'Contactez nos experts pour une investigation approfondie' 
                : 'Contact our experts for a thorough investigation'}
            </p>
            <button className="bg-white text-blue-600 px-8 py-3 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105">
              {language === 'fr' ? 'Nous Contacter' : 'Contact Us'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}